import { useRef } from "react";
import PropTypes from "prop-types";
import styles from "./calculator.module.scss";

const ANTIBIOTICS = [
  { key: "Amikacin", label: "Amikacin" },
  { key: "Gentamicin", label: "Gentamicin" },
  { key: "Tobramycin", label: "Tobramycin" },
];

const AminoglycosideDescription = ({ name, doses, icuDose }) => (
  <div>
    <strong>{name}</strong>
    <br />
    <p>
      For patients without septic shock and not requiring intensive care support, the dose depends
      on their creatinine clearance (CrCl):
    </p>
    <ul>
      <li>CrCl more than 60 mL/min: {doses[0]}</li>
      <li>CrCl 40 to 60 mL/min: {doses[1]}</li>
      <li>CrCl less than 40 mL/min: {doses[2]}</li>
    </ul>
    <p>
      For patients with septic shock or requiring intensive care support, a dose of {icuDose} is
      used regardless of kidney function.{" "}
    </p>
    <p>
      In critically ill patients, individualised dose adjustment may be necessary to achieve
      pharmacokinetic/pharmacodynamic (PK/PD) targets. This may require a higher LBW-based mg/kg
      dose and should always be guided by expert advice and early therapeutic drug monitoring (TDM).
    </p>
  </div>
);

const GENTA_DESC = (
  <AminoglycosideDescription
    name="Gentamicin"
    doses={["7 mg/kg up to 680 mg", "5 mg/kg up to 480 mg", "4 mg/kg up to 380 mg"]}
    icuDose="7 mg/kg"
  />
);

const TOBRA_DESC = (
  <AminoglycosideDescription
    name="Tobramycin"
    doses={["7 mg/kg up to 680 mg", "5 mg/kg up to 480 mg", "4 mg/kg up to 380 mg"]}
    icuDose="7 mg/kg"
  />
);

const AMIKA_DESC = (
  <AminoglycosideDescription
    name="Amikacin"
    doses={["30 mg/kg up to 2.5 g", "20 mg/kg up to 1.625 g", "15 mg/kg up to 1.25 g"]}
    icuDose="30 mg/kg"
  />
);

const ANTIBIOTIC_DESCRIPTIONS = {
  Gentamicin: GENTA_DESC,
  Tobramycin: TOBRA_DESC,
  Amikacin: AMIKA_DESC,
};

export default function AntibioticsSelector({ value, onChange, error }) {
  const firstBtnRef = useRef(null);

  return (
    <div className={styles.antibioticSwitcherPanel}>
      <label className={styles.fieldLabel} style={{ marginBottom: 0 }}>
        Antibiotic
      </label>
      <div className={styles.antibioticSwitcher}>
        {ANTIBIOTICS.map((abx, i) => (
          <button
            key={abx.key}
            type="button"
            id={i === 0 ? "abx-antibiotic" : undefined} // Add ID to first button for focus hook
            className={`${styles.antibioticBtn} ${
              value === abx.key ? styles.antibioticBtnActive : ""
            } ${error ? styles.inputContainerError : ""}`}
            onClick={() => onChange(abx.key)}
            aria-pressed={value === abx.key}
            ref={i === 0 ? firstBtnRef : undefined}
          >
            {abx.label}
          </button>
        ))}
      </div>
      {value && (
        <div className={styles.antibioticDescriptionPanel}>{ANTIBIOTIC_DESCRIPTIONS[value]}</div>
      )}
      {error && <div className={styles.error}>{error}</div>}
    </div>
  );
}

AntibioticsSelector.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.string,
};
